import { useEffect, useRef } from 'react';
import {
  Container,
  Title,
  Box,
  Image,
  em,
  Stack,
  Group,
  Text
} from "@mantine/core";
import { useDispatch, useSelector } from 'react-redux';
import { getPublicSettings } from '../../redux/reducers/publicSlice';
import { useMediaQuery } from '@mantine/hooks';
import { selectLandingPageContent, selectShowGroupDealsBanner, selectVolcanoBannerModalSettings } from '../../redux/selectors/publicSelector';
import { IconStarFilled } from '@tabler/icons-react';
import HeroSection from "./hero/hero.section";
import MoreInfoPage from "./more-info/more-info.page";
import CarouselSection from "./carousel/carousel.section";
import FooterSection from './footer/footer.section';
import ReservationPage from './reservation/reservation.page';
import TheaterMapPage from './map/theater-map.page';
import PricingSection from "./pricing/pricing.section";
import ReviewsSection from "./reviews/reviews.section";
import BannerSection from './banner/banner.section';
import VolcanoBannerSection from './volcano-banner/volcano-banner.section';
import PromoBannerModal from './promo-banner-modal/promo-banner-modal';
import colors from '../../utils/styles';
import "./landing-page.css";

// PINK Colors from ex-site flowers
// Main bright pink/magenta: #FF1493 (Deep Pink)
// Lighter pink highlights: #FF69B4 (Hot Pink)
// Darker pink shadows: #C71585 (Medium Violet Red)

function LandingPage() {
  const dispatch = useDispatch();
  // Add ref for the calendar & map section
  const calendarRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const landingPageContent = useSelector(selectLandingPageContent);
  const showGroupDealsBanner = useSelector(selectShowGroupDealsBanner);
  const volcanoBanner = useSelector(selectVolcanoBannerModalSettings);
  
  const isDesktop = useMediaQuery(`(max-width: ${em(1024)})`);

  // Add scroll functions
  const scrollToCalendar = () => {
    calendarRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  const scrollToMap = () => {
    mapRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    dispatch(getPublicSettings());
  }, []);


  const fiveStars = Array.from({ length: 5 }, (_, index) => (
    <IconStarFilled
      key={index}
      size={40}
      style={index === 4 ? { marginRight: isDesktop ? '15px' : '25px' } : {}}
    />
  ));

  const fiveStarsRight = Array.from({ length: 5 }, (_, index) => (
    <IconStarFilled
      key={index}
      size={40}
      style={index === 0 ? { marginLeft: isDesktop ? '15px' : '25px' } : {}}
    />
  ));

  return (
    <div className="landing-page">
      {window.location.host.includes('heroku') && <Group justify="center" bg="red" pos="fixed" top={0} left={0} right={0} style={{ zIndex: 1000 }}>
        <Text>WARNING: This is a test environment. Changes here do NOT reflect the real environment.</Text>
      </Group>}
      <HeroSection scrollToCalendar={scrollToCalendar} scrollToMap={scrollToMap} />

      {/* Summer group deals banner */}
      {showGroupDealsBanner && <BannerSection />}

      {/* Volcano day banner */}
      {volcanoBanner.enabled && <VolcanoBannerSection />}

      {/* Configurable promotional banner modal */}
      <PromoBannerModal />

      {/* Main Content */}
      <Container size="xl">
        <Stack justify='center'>
          <Title style={{ color: colors.appYellow, fontFamily: "Playfair Display", fontSize: 'clamp(24px, 5vw, 70px)', display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', whiteSpace: 'nowrap', gap: '8px' }}>{fiveStars} SANTORINI {fiveStarsRight}</Title>
          <Title style={{ color: colors.appYellow, fontFamily: "Playfair Display", fontSize: 'clamp(24px, 5vw, 58px)' }}>THE GREEK WEDDING SHOW</Title>
        </Stack>
        <Box
          className="landing-page-title-container"
          style={{ border: colors.appBorder, borderRadius: '25px' }}
        >
          <Title
            size={60}
            style={{
              color: colors.appYellow,
              fontFamily: "Playfair Display",
              transform: 'scaleY(1.2)',
              letterSpacing: '0.07em',
              fontSize: 'clamp(24px, 5vw, 60px)'
            }}
          >
            {landingPageContent.title}
          </Title>
          <Title
            my="lg"
            size={48}
            style={{
              color: colors.appYellow,
              fontFamily: "Playfair Display",
              marginBottom: 0,
              transform: 'scaleY(1.1)',
              letterSpacing: '0.05em',
              fontSize: 'clamp(18px, 4vw, 48px)'
            }}
          >
            {landingPageContent.subtitle}
          </Title>
          <Title
            size={60}
            style={{
              color: colors.appYellow,
              fontFamily: "Playfair Display",
              marginTop: -20,
              transform: 'scaleY(1.2)',
              letterSpacing: '0.07em',
              fontSize: 'clamp(24px, 5vw, 60px)'
            }}
          >
            {landingPageContent.closing}
          </Title>
        </Box>

        {/* Carousel Section */}
        <CarouselSection />

        {/* Reviews Section */}
        {!isDesktop && <ReviewsSection />}

        {/* Pricing Section */}
        <PricingSection scrollToCalendar={scrollToCalendar} />
      </Container>

      {!isDesktop && <Image
        src="/assets/images/feb_images/photo-1_red.jpg"
        alt="divider"
      />}

      <ReservationPage calendarRef={calendarRef} />

      {isDesktop && <Image
        src="/assets/images/feb_images/photo-1_red.jpg"
        alt="divider"
        style={{ marginTop: '4%' }}
      />}

      <TheaterMapPage mapRef={mapRef} />

      <MoreInfoPage />
      <FooterSection />
    </div >
  );
}

export default LandingPage;